import { Dispatch, SetStateAction } from "react";

import {
  ProjectIssuanceBNGPanelData,
  ProjectIssuanceCarbonPanelData,
  ProjectIssuanceSpecificProperty,
  ProjectIssuanceSpecificSchema,
} from "../../../../../models";
import { camelCaseToSentenceCase } from "../../../../../utils";

export type IssuancePanelData = ProjectIssuanceBNGPanelData | ProjectIssuanceCarbonPanelData;
export type StandardSpecificIssuanceData = IssuancePanelData["standardSpecificIssuanceData"];

const COMMON_ISSUANCE_FIELD_KEYS = new Set([
  "uuid",
  "status",
  "issuanceDate",
  "quantityType",
  "soldQuantity",
  "reservedQuantity",
  "availableQuantity",
  "serialNumber",
  "price",
  "priceCurrency",
  "rowVersion",
  "verifierUuid",
  "totalQuantity",
]);

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  createdOrEnhanced: "Created or enhanced",
  habitatDistinctiveness: "Distinctiveness",
  habitatType: "Habitat group",
  quantityType: "Unit type",
  vintageEndDate: "Vintage end",
  vintageStartDate: "Vintage start",
};

export const isReadOnlyOrConstField = (definition: ProjectIssuanceSpecificProperty): boolean => {
  return definition.readOnly === true || definition.const !== undefined;
};

export const shouldRenderSchemaField = (fieldKey: string, definition: ProjectIssuanceSpecificProperty): boolean => {
  return !COMMON_ISSUANCE_FIELD_KEYS.has(fieldKey) && !isReadOnlyOrConstField(definition);
};

export const normaliseFieldLabel = (fieldKey: string): string => {
  if (FIELD_LABEL_OVERRIDES[fieldKey]) {
    return FIELD_LABEL_OVERRIDES[fieldKey];
  }

  return camelCaseToSentenceCase(fieldKey);
};

export const getIssuanceFieldValue = (issuanceData: IssuancePanelData, fieldKey: string): unknown => {
  const standardSpecificIssuanceData = issuanceData.standardSpecificIssuanceData as unknown as Record<string, unknown>;

  if (Object.prototype.hasOwnProperty.call(standardSpecificIssuanceData, fieldKey)) {
    return standardSpecificIssuanceData[fieldKey];
  }

  return (issuanceData as unknown as Record<string, unknown>)[fieldKey];
};

export const setIssuanceFieldValue = (
  setIssuanceData: Dispatch<SetStateAction<IssuancePanelData>>,
  fieldKey: string,
  value: unknown
): void => {
  setIssuanceData((previousData) => {
    const updatedStandardSpecificIssuanceData = {
      ...(previousData.standardSpecificIssuanceData as unknown as Record<string, unknown>),
      [fieldKey]: value,
    } as unknown as StandardSpecificIssuanceData;

    return {
      ...previousData,
      standardSpecificIssuanceData: updatedStandardSpecificIssuanceData,
    } as IssuancePanelData;
  });
};

export const getConditionalEnumValues = (
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined,
  fieldKey: string,
  getFieldValue: (field: string) => unknown
): string[] | undefined => {
  if (!issuanceSchema?.allOf || issuanceSchema.allOf.length === 0) {
    return undefined;
  }

  const matchingCondition = issuanceSchema.allOf.find((condition) => {
    const conditionProperties = condition.if?.properties;

    if (!conditionProperties) {
      return false;
    }

    return Object.entries(conditionProperties).every(([conditionFieldKey, conditionDefinition]) => {
      if (conditionDefinition.const === undefined) {
        return true;
      }

      const conditionValue = getFieldValue(conditionFieldKey);
      return conditionValue != null && String(conditionValue) === conditionDefinition.const;
    });
  });

  return matchingCondition?.then?.properties?.[fieldKey]?.enum;
};
